import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthCallback from './components/AuthCallback';
import { CurrencyProvider } from './context/CurrencyContext';
import { Toaster } from './components/ui/sonner';
import InstallPrompt from './components/InstallPrompt';
import InstallBanner from './components/InstallBanner';
import OfflineIndicator from './components/OfflineIndicator';
import TeenVestLoader from './components/TeenVestLoader';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Market from './pages/Market';
import Portfolio from './pages/Portfolio';
import Leaderboard from './pages/Leaderboard';
import Learn from './pages/Learn';
import Help from './pages/Help';
import GameModes from './pages/GameModes';
import LearnMode from './pages/LearnMode';
import ChaosMode from './pages/ChaosMode';
import NewsReactor from './pages/NewsReactor';
import News from './pages/News';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import OracleMode from './pages/OracleMode';
import WolfMode from './pages/WolfMode';
import AIBubbleMode from './pages/AIBubbleMode';
import ResetApprove from './pages/ResetApprove';
import Community from './pages/Community';
import Bets from './pages/Bets';
import About from './pages/About';
import Blog from './pages/Blog';
import CentralMessage from './pages/CentralMessage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center">
        <TeenVestLoader size={104} label="Loading..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)

  // Google OAuth callback: process session_id BEFORE any auth checks/routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen noise-bg flex items-center justify-center">
        <TeenVestLoader size={104} label="Loading..." />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/dashboard" replace /> : <Landing />}
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/market"
        element={
          <ProtectedRoute>
            <Market />
          </ProtectedRoute>
        }
      />
      <Route
        path="/market/:symbol"
        element={
          <ProtectedRoute>
            <Market />
          </ProtectedRoute>
        }
      />
      <Route
        path="/portfolio"
        element={
          <ProtectedRoute>
            <Portfolio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn"
        element={
          <ProtectedRoute>
            <Learn />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        }
      />
      <Route
        path="/news"
        element={
          <ProtectedRoute>
            <News />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes"
        element={
          <ProtectedRoute>
            <GameModes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/learn"
        element={
          <ProtectedRoute>
            <LearnMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/chaos"
        element={
          <ProtectedRoute>
            <ChaosMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/news-reactor"
        element={
          <ProtectedRoute>
            <NewsReactor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/oracle"
        element={
          <ProtectedRoute>
            <OracleMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/wolf"
        element={
          <ProtectedRoute>
            <WolfMode />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-modes/aibubble"
        element={
          <ProtectedRoute>
            <AIBubbleMode />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<Admin />} />
      <Route path="/reset-approve" element={<ResetApprove />} />
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/central-message" element={<CentralMessage />} />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <Community />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bets"
        element={
          <ProtectedRoute>
            <Bets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

// Install prompts should ONLY appear for unauthenticated visitors (Landing / signup).
// Once the user has signed up or logged in, we auto-remove them.
const InstallPromptsForGuests = () => {
  const { user } = useAuth();
  if (user) return null;
  return (
    <>
      <InstallBanner />
      <InstallPrompt />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CurrencyProvider>
          <div className="App">
            <OfflineIndicator />
            <InstallPromptsForGuests />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </CurrencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;